import type { Building } from "building-types/building/buildingTypes";
import type { EdgeData } from "building-types/provenance/halfEdgeDataTypes";
import { getWallPath } from "building-types/walls/wallUtils";

interface DimensionWithHalfEdges {
  readonly halfEdges?: readonly EdgeData[];
}

/**
 * Returns true when a dimension is anchored entirely within the selected reporting unit. Each
 * anchoring half-edge is resolved to the rooms on either side of its wall; the dimension is kept
 * only when at least one room resolves and every resolved room belongs to the unit. Half-edges
 * that don't resolve to a wall/room are ignored; a dimension that resolves to no rooms is dropped.
 */
function dimensionIsInReportingUnit(
  dimension: DimensionWithHalfEdges,
  building: Building,
  reportingUnitRoomIds: ReadonlySet<string>,
): boolean {
  const halfEdges = dimension.halfEdges ?? [];
  let resolvedAnyRoom = false;

  for (const edge of halfEdges) {
    if (!edge || edge.halfEdge === undefined) {
      continue;
    }
    const wallSystem = building.walls[edge.levelId];
    if (!wallSystem) {
      continue;
    }
    const wallId = wallSystem.halfEdgeToWall[edge.halfEdge];
    const wall = wallId !== undefined ? wallSystem.walls[wallId] : undefined;
    if (!wall) {
      continue;
    }

    const { leftRoom, rightRoom } = getWallPath(wall);
    for (const room of [leftRoom, rightRoom]) {
      if (!room) {
        continue;
      }
      resolvedAnyRoom = true;
      if (!reportingUnitRoomIds.has(room.id)) {
        // Anchored to a room outside the unit, so the dimension is not fully within it.
        return false;
      }
    }
  }

  return resolvedAnyRoom;
}

/**
 * Filters interior dimensions down to those anchored entirely within the selected
 * reporting unit. See {@link dimensionIsInReportingUnit} for the per-dimension rule.
 */
export function filterInteriorDimensionsToReportingUnit<T extends DimensionWithHalfEdges>(
  dimensions: readonly T[],
  building: Building,
  reportingUnitRoomIds: ReadonlySet<string>,
): T[] {
  return dimensions.filter((dimension) => dimensionIsInReportingUnit(dimension, building, reportingUnitRoomIds));
}
